import { supabase } from '../lib/supabase';

/**
 * Staff Management Service
 * Handles all database operations for staff management
 */
export const staffService = {
  
  /**
   * Get all staff members with their user profiles and event assignments
   */
  async getAllStaff(userProfile) {
    try {
      // Check if user has permission to view staff
      if (!userProfile || (userProfile?.role !== 'host' && userProfile?.role !== 'admin')) {
        throw new Error('Unauthorized: Only hosts and admins can view staff');
      }

      const { data, error } = await supabase?.from('staff')?.select(`
          *,
          user_profiles!staff_user_id_fkey(
            id,
            full_name,
            email,
            phone,
            avatar_url,
            first_name,
            last_name
          ),
          staff_event_assignments(
            id,
            event_id,
            status,
            role_description,
            shift_start,
            shift_end,
            assignment_date,
            check_in_time,
            check_out_time,
            notes,
            events(
              id,
              title,
              event_date,
              location,
              venue_name
            )
          ),
          staff_availability(
            id,
            day_of_week,
            start_time,
            end_time,
            is_available
          )
        `)?.order('created_at', { ascending: false });

      if (error) throw error;

      return { data: data || [], error: null };
    } catch (error) {
      console.error('Error fetching staff:', error);
      return { data: [], error: error?.message };
    }
  },

  /**
   * Get staff member by ID
   */
  async getStaffById(staffId, userProfile) {
    try {
      if (!userProfile || (userProfile?.role !== 'host' && userProfile?.role !== 'admin' && userProfile?.id !== staffId)) {
        throw new Error('Unauthorized');
      }

      const { data, error } = await supabase?.from('staff')?.select(`
          *,
          user_profiles!staff_user_id_fkey(*),
          staff_event_assignments(
            *,
            events(*)
          ),
          staff_availability(*)
        `)?.eq('id', staffId)?.single();

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Error fetching staff by ID:', error);
      return { data: null, error: error?.message };
    }
  },

  /**
   * Create new staff member
   */
  async createStaff(staffData, userProfile) {
    try {
      if (!userProfile || (userProfile?.role !== 'host' && userProfile?.role !== 'admin')) {
        throw new Error('Unauthorized: Only hosts and admins can create staff');
      }

      // Check if user already exists
      const { data: existingUser, error: checkError } = await supabase?.from('user_profiles')?.select('*')?.eq('email', staffData?.email)?.single();

      let userProfileId;

      if (checkError && checkError?.code !== 'PGRST116') { // PGRST116 = no rows returned
        throw checkError;
      }

      if (existingUser) {
        userProfileId = existingUser?.id;
      } else {
        // Generate UUID for the new user profile
        const { data: { user }, error: createAuthUserError } = await supabase?.auth?.admin?.createUser({
          email: staffData?.email,
          email_confirm: true,
          user_metadata: {
            full_name: staffData?.full_name,
            role: 'attendee'
          }
        });

        if (createAuthUserError) {
          // If auth user creation fails, fall back to manual ID generation
          console.warn('Auth user creation failed, using manual ID generation:', createAuthUserError);
          
          // Use browser's crypto API for UUID generation
          userProfileId = self.crypto?.randomUUID();
          
          // Create user profile with manual ID - ensuring proper reference to user_profiles table
          const { data: newUser, error: createUserError } = await supabase?.from('user_profiles')?.insert([{
              id: userProfileId,
              full_name: staffData?.full_name,
              email: staffData?.email,
              phone: staffData?.phone,
              first_name: staffData?.first_name || staffData?.full_name?.split(' ')?.[0],
              last_name: staffData?.last_name || staffData?.full_name?.split(' ')?.slice(1)?.join(' '),
              role: 'attendee', // Default role for staff users
              is_active: true
            }])?.select()?.single();

          if (createUserError) {
            console.error('User profile creation error:', createUserError);
            throw new Error(`Failed to create user profile: ${createUserError?.message}`);
          }
          userProfileId = newUser?.id;
        } else {
          // Use the auth user ID and create profile
          userProfileId = user?.id;
          const { data: newUser, error: createUserError } = await supabase?.from('user_profiles')?.insert([{
              id: userProfileId,
              full_name: staffData?.full_name,
              email: staffData?.email,
              phone: staffData?.phone,
              first_name: staffData?.first_name || staffData?.full_name?.split(' ')?.[0],
              last_name: staffData?.last_name || staffData?.full_name?.split(' ')?.slice(1)?.join(' '),
              role: 'attendee', // Default role for staff users
              is_active: true
            }])?.select()?.single();

          if (createUserError) {
            console.error('User profile creation error:', createUserError);
            throw new Error(`Failed to create user profile: ${createUserError?.message}`);
          }
        }
      }

      // Create staff record with proper staff_role (including checkin_staff support)
      const staffRole = staffData?.staff_role || 'check_in_staff'; // Default to checkin staff
      const { data: newStaff, error: staffError } = await supabase?.from('staff')?.insert([{
          user_id: userProfileId,
          staff_role: staffRole,
          hire_date: staffData?.hire_date,
          phone_number: staffData?.phone_number || staffData?.phone,
          emergency_contact: staffData?.emergency_contact,
          notes: staffData?.notes,
          skills: staffData?.skills || [],
          hourly_rate: staffData?.hourly_rate ? parseFloat(staffData?.hourly_rate) : null,
          status: staffData?.status || 'active'
        }])?.select(`
          *,
          user_profiles!staff_user_id_fkey(*)
        `)?.single();

      if (staffError) {
        console.error('Staff creation error:', staffError);
        throw new Error(`Failed to create staff member: ${staffError?.message}`);
      }

      return { data: newStaff, error: null };
    } catch (error) {
      console.error('Error creating staff:', error);
      return { data: null, error: error?.message };
    }
  },

  /**
   * Update staff member
   */
  async updateStaff(staffId, updates, userProfile) {
    try {
      if (!userProfile || (userProfile?.role !== 'host' && userProfile?.role !== 'admin')) {
        throw new Error('Unauthorized: Only hosts and admins can update staff');
      }

      const { data, error } = await supabase?.from('staff')?.update({
          ...updates,
          updated_at: new Date()?.toISOString()
        })?.eq('id', staffId)?.select(`
          *,
          user_profiles!staff_user_id_fkey(*)
        `)?.single();

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Error updating staff:', error);
      return { data: null, error: error?.message };
    }
  },

  /**
   * Delete staff member
   */
  async deleteStaff(staffId, userProfile) {
    try {
      if (!userProfile || (userProfile?.role !== 'host' && userProfile?.role !== 'admin')) {
        throw new Error('Unauthorized: Only hosts and admins can delete staff');
      }

      const { error } = await supabase?.from('staff')?.delete()?.eq('id', staffId);

      if (error) throw error;

      return { error: null };
    } catch (error) {
      console.error('Error deleting staff:', error);
      return { error: error?.message };
    }
  },

  /**
   * Assign staff to event
   */
  async assignStaffToEvent(assignmentData, userProfile) {
    try {
      if (!userProfile || (userProfile?.role !== 'host' && userProfile?.role !== 'admin')) {
        throw new Error('Unauthorized: Only hosts and admins can assign staff');
      }

      const assignments = Array.isArray(assignmentData?.staffIds) 
        ? assignmentData?.staffIds?.map(staffId => ({
            staff_id: staffId,
            event_id: assignmentData?.eventId,
            role_description: assignmentData?.roleDescription,
            shift_start: assignmentData?.shiftStart,
            shift_end: assignmentData?.shiftEnd,
            notes: assignmentData?.notes,
            assigned_by: userProfile?.id,
            status: 'assigned'
          }))
        : [{
            staff_id: assignmentData?.staffId,
            event_id: assignmentData?.eventId,
            role_description: assignmentData?.roleDescription,
            shift_start: assignmentData?.shiftStart,
            shift_end: assignmentData?.shiftEnd,
            notes: assignmentData?.notes,
            assigned_by: userProfile?.id,
            status: 'assigned'
          }];

      const { data, error } = await supabase?.from('staff_event_assignments')?.insert(assignments)?.select(`
          *,
          staff(
            *,
            user_profiles(*)
          ),
          events(*)
        `);

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Error assigning staff to event:', error);
      return { data: null, error: error?.message };
    }
  },

  /**
   * Update staff assignment
   */
  async updateStaffAssignment(assignmentId, updates, userProfile) {
    try {
      if (!userProfile || (userProfile?.role !== 'host' && userProfile?.role !== 'admin')) {
        throw new Error('Unauthorized');
      }

      const { data, error } = await supabase?.from('staff_event_assignments')?.update({
          ...updates,
          updated_at: new Date()?.toISOString()
        })?.eq('id', assignmentId)?.select(`
          *,
          staff(*),
          events(*)
        `)?.single();

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Error updating staff assignment:', error);
      return { data: null, error: error?.message };
    }
  },

  /**
   * Remove staff assignment
   */
  async removeStaffAssignment(assignmentId, userProfile) {
    try {
      if (!userProfile || (userProfile?.role !== 'host' && userProfile?.role !== 'admin')) {
        throw new Error('Unauthorized');
      }

      const { error } = await supabase?.from('staff_event_assignments')?.delete()?.eq('id', assignmentId);

      if (error) throw error;

      return { error: null };
    } catch (error) {
      console.error('Error removing staff assignment:', error);
      return { error: error?.message };
    }
  },

  /**
   * Create new assignment for existing staff
   */
  async createStaffAssignment(assignmentData, userProfile) {
    try {
      if (!userProfile || (userProfile?.role !== 'host' && userProfile?.role !== 'admin')) {
        throw new Error('Unauthorized: Only hosts and admins can assign staff');
      }

      const { data, error } = await supabase?.from('staff_event_assignments')?.insert([{
          staff_id: assignmentData?.staffId,
          event_id: assignmentData?.eventId,
          role_description: assignmentData?.roleDescription,
          shift_start: assignmentData?.shiftStart,
          shift_end: assignmentData?.shiftEnd,
          notes: assignmentData?.notes,
          assigned_by: userProfile?.id,
          status: 'assigned'
        }])?.select(`
          *,
          staff(
            *,
            user_profiles(*)
          ),
          events(*)
        `)?.single();

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Error creating staff assignment:', error);
      return { data: null, error: error?.message };
    }
  },

  /**
   * Get events for assignment dropdown
   */
  async getEventsForAssignment(userProfile) {
    try {
      if (!userProfile || (userProfile?.role !== 'host' && userProfile?.role !== 'admin')) {
        throw new Error('Unauthorized');
      }

      let query = supabase?.from('events')?.select('id, title, event_date, end_date, location, venue_name, host_id')?.gte('event_date', new Date()?.toISOString())?.order('event_date', { ascending: true });

      // Hosts can only see their own events for assignment
      if (userProfile?.role === 'host') {
        query = query?.eq('host_id', userProfile?.id);
      }

      const { data, error } = await query;

      if (error) throw error;

      return { data: data || [], error: null };
    } catch (error) {
      console.error('Error fetching events for assignment:', error);
      return { data: [], error: error?.message };
    }
  },

  /**
   * Get staff statistics
   */
  async getStaffStats(userProfile) {
    try {
      if (!userProfile || (userProfile?.role !== 'host' && userProfile?.role !== 'admin')) {
        throw new Error('Unauthorized');
      }

      // Get staff counts by status
      const { data: staffData, error: staffError } = await supabase?.from('staff')?.select('status, staff_event_assignments(id, status, events(event_date))');

      if (staffError) throw staffError;

      const totalStaff = staffData?.length || 0;
      const activeStaff = staffData?.filter(member => member?.status === 'active')?.length || 0;
      const inactiveStaff = staffData?.filter(member => member?.status === 'inactive')?.length || 0;
      const suspendedStaff = staffData?.filter(member => member?.status === 'suspended')?.length || 0;

      // Count assignments
      const assignedStaff = staffData?.filter(member => 
        member?.staff_event_assignments?.some(assignment => 
          assignment?.status === 'assigned' && 
          new Date(assignment.events?.event_date) > new Date()
        )
      )?.length || 0;

      const availableStaff = activeStaff - assignedStaff;

      return {
        data: {
          totalStaff,
          activeStaff,
          inactiveStaff,
          suspendedStaff,
          assignedStaff,
          availableStaff
        },
        error: null
      };
    } catch (error) {
      console.error('Error fetching staff stats:', error);
      return {
        data: {
          totalStaff: 0,
          activeStaff: 0,
          inactiveStaff: 0,
          suspendedStaff: 0,
          assignedStaff: 0,
          availableStaff: 0
        },
        error: error?.message
      };
    }
  },

  /**
   * Bulk update staff status
   */
  async bulkUpdateStaffStatus(staffIds, status, userProfile) {
    try {
      if (!userProfile || (userProfile?.role !== 'host' && userProfile?.role !== 'admin')) {
        throw new Error('Unauthorized');
      }

      const { data, error } = await supabase?.from('staff')?.update({ 
          status,
          updated_at: new Date()?.toISOString()
        })?.in('id', staffIds)?.select();

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Error bulk updating staff status:', error);
      return { data: null, error: error?.message };
    }
  },

  /**
   * Check staff availability
   */
  async checkStaffAvailability(staffId, eventDate) {
    try {
      const dayOfWeek = new Date(eventDate)?.getDay();
      
      const { data, error } = await supabase?.from('staff_availability')?.select('*')?.eq('staff_id', staffId)?.eq('day_of_week', dayOfWeek)?.eq('is_available', true);

      if (error) throw error;

      return { available: data && data?.length > 0, data, error: null };
    } catch (error) {
      console.error('Error checking staff availability:', error);
      return { available: false, data: null, error: error?.message };
    }
  },

  /**
   * Get available staff role options
   */
  async getStaffRoleOptions() {
    try {
      const { data, error } = await supabase?.rpc('get_staff_role_options');
      
      if (error) throw error;
      
      return { 
        data: data || [
          'check_in_staff',
          'security_staff', 
          'technical_staff',
          'customer_service_staff',
          'coordinator_staff',
          'supervisor_staff'
        ], 
        error: null 
      };
    } catch (error) {
      console.error('Error fetching staff role options:', error);
      // Fallback to default options if RPC fails
      return { 
        data: [
          'check_in_staff',
          'security_staff', 
          'technical_staff',
          'customer_service_staff',
          'coordinator_staff',
          'supervisor_staff'
        ], 
        error: null 
      };
    }
  },

  /**
   * Validate staff assignment to event
   */
  async validateStaffEventAssignment(staffId, eventId) {
    try {
      const { data, error } = await supabase?.rpc('can_assign_staff_to_event', {
        staff_uuid: staffId,
        event_uuid: eventId
      });

      if (error) throw error;

      return { canAssign: data === true, error: null };
    } catch (error) {
      console.error('Error validating staff assignment:', error);
      return { canAssign: false, error: error?.message };
    }
  },

  /**
   * Set or update password for staff member
   */
  async setStaffPassword(staffId, newPassword, userProfile) {
    try {
      if (!userProfile || (userProfile?.role !== 'host' && userProfile?.role !== 'admin')) {
        throw new Error('Unauthorized: Only hosts and admins can set staff passwords');
      }

      // Get staff member details
      const { data: staffMember, error: staffError } = await supabase?.from('staff')?.select(`
          *,
          user_profiles!staff_user_id_fkey(*)
        `)?.eq('id', staffId)?.single();

      if (staffError) throw staffError;
      if (!staffMember) throw new Error('Staff member not found');

      // Update password in Supabase Auth
      const { error: updateError } = await supabase?.auth?.admin?.updateUser(
        staffMember?.user_id,
        { password: newPassword }
      );

      if (updateError) {
        console.warn('Admin password update failed, trying user update:', updateError);
        // Fallback: If admin update fails, try updating via auth
        const { error: authError } = await supabase?.auth?.updateUser({ 
          password: newPassword 
        });
        if (authError) throw new Error(`Failed to update password: ${authError?.message}`);
      }

      return { success: true, error: null };
    } catch (error) {
      console.error('Error setting staff password:', error);
      return { success: false, error: error?.message };
    }
  },

  /**
   * Create staff member with password
   */
  async createStaffWithPassword(staffData, password, userProfile) {
    try {
      if (!userProfile || (userProfile?.role !== 'host' && userProfile?.role !== 'admin')) {
        throw new Error('Unauthorized: Only hosts and admins can create staff');
      }

      if (!password || password?.length < 6) {
        throw new Error('Password must be at least 6 characters long');
      }

      // Create auth user with password
      const { data: { user }, error: createUserError } = await supabase?.auth?.admin?.createUser({
        email: staffData?.email,
        password: password,
        email_confirm: true,
        user_metadata: {
          full_name: staffData?.full_name,
          role: 'attendee' // Staff are treated as attendees in the auth system
        }
      });

      if (createUserError) {
        console.error('Auth user creation error:', createUserError);
        throw new Error(`Failed to create staff user: ${createUserError?.message}`);
      }

      if (!user) throw new Error('Failed to create user');

      // Create user profile
      const { data: newUserProfile, error: profileError } = await supabase?.from('user_profiles')?.insert([{
          id: user?.id,
          full_name: staffData?.full_name,
          email: staffData?.email,
          phone: staffData?.phone,
          first_name: staffData?.first_name || staffData?.full_name?.split(' ')?.[0],
          last_name: staffData?.last_name || staffData?.full_name?.split(' ')?.slice(1)?.join(' '),
          role: 'attendee',
          is_active: true
        }])?.select()?.single();

      if (profileError) {
        console.error('User profile creation error:', profileError);
        throw new Error(`Failed to create user profile: ${profileError?.message}`);
      }

      // Create staff record
      const staffRole = staffData?.staff_role || 'check_in_staff';
      const { data: newStaff, error: staffError } = await supabase?.from('staff')?.insert([{
          user_id: user?.id,
          staff_role: staffRole,
          hire_date: staffData?.hire_date,
          phone_number: staffData?.phone_number || staffData?.phone,
          emergency_contact: staffData?.emergency_contact,
          notes: staffData?.notes,
          skills: staffData?.skills || [],
          hourly_rate: staffData?.hourly_rate ? parseFloat(staffData?.hourly_rate) : null,
          status: staffData?.status || 'active'
        }])?.select(`
          *,
          user_profiles!staff_user_id_fkey(*)
        `)?.single();

      if (staffError) {
        console.error('Staff creation error:', staffError);
        throw new Error(`Failed to create staff member: ${staffError?.message}`);
      }

      return { data: newStaff, error: null };
    } catch (error) {
      console.error('Error creating staff with password:', error);
      return { data: null, error: error?.message };
    }
  },

  /**
   * Staff login verification
   */
  async verifyStaffLogin(email, password) {
    try {
      // Sign in with Supabase Auth
      const { data, error } = await supabase?.auth?.signInWithPassword({
        email: email?.trim(),
        password
      });

      if (error) throw error;

      // Check if user is a staff member
      const { data: staffMember, error: staffError } = await supabase?.from('staff')?.select(`
          *,
          user_profiles!staff_user_id_fkey(*)
        `)?.eq('user_id', data?.user?.id)?.single();

      if (staffError || !staffMember) {
        // Sign out if not a staff member
        await supabase?.auth?.signOut();
        throw new Error('Invalid staff credentials');
      }

      if (staffMember?.status !== 'active') {
        await supabase?.auth?.signOut();
        throw new Error('Staff account is not active');
      }

      return { 
        user: data?.user, 
        staffMember,
        error: null 
      };
    } catch (error) {
      console.error('Staff login error:', error);
      return { 
        user: null, 
        staffMember: null, 
        error: error?.message || 'Failed to login' 
      };
    }
  },

  /**
   * Get staff member by user ID (for authenticated staff)
   */
  async getStaffByUserId(userId) {
    try {
      const { data, error } = await supabase?.from('staff')?.select(`
          *,
          user_profiles(*),
          staff_event_assignments(
            *,
            events(*)
          )
        `)?.eq('user_id', userId)?.single();

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Error fetching staff by user ID:', error);
      return { data: null, error: error?.message };
    }
  },

  /**
   * Record staff check-in for event assignment
   */
  async checkInStaff(assignmentId, userProfile) {
    try {
      if (!userProfile) {
        throw new Error('Unauthorized: User must be logged in');
      }

      const checkInTime = new Date()?.toISOString();
      
      const { data, error } = await supabase
        ?.from('staff_event_assignments')
        ?.update({
          check_in_time: checkInTime,
          status: 'checked_in',
          updated_at: checkInTime
        })
        ?.eq('id', assignmentId)
        ?.select(`
          *,
          staff(
            *,
            user_profiles(*)
          ),
          events(*)
        `)
        ?.single();

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Error checking in staff:', error);
      return { data: null, error: error?.message };
    }
  },

  /**
   * Record staff check-out for event assignment
   */
  async checkOutStaff(assignmentId, userProfile) {
    try {
      if (!userProfile) {
        throw new Error('Unauthorized: User must be logged in');
      }

      const checkOutTime = new Date()?.toISOString();
      
      const { data, error } = await supabase
        ?.from('staff_event_assignments')
        ?.update({
          check_out_time: checkOutTime,
          status: 'completed',
          updated_at: checkOutTime
        })
        ?.eq('id', assignmentId)
        ?.select(`
          *,
          staff(
            *,
            user_profiles(*)
          ),
          events(*)
        `)
        ?.single();

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Error checking out staff:', error);
      return { data: null, error: error?.message };
    }
  },

  /**
   * Get current assignment for staff member
   */
  async getCurrentAssignment(staffId) {
    try {
      const now = new Date()?.toISOString();
      
      const { data, error } = await supabase
        ?.from('staff_event_assignments')
        ?.select(`
          *,
          staff(
            *,
            user_profiles(*)
          ),
          events(*)
        `)
        ?.eq('staff_id', staffId)
        ?.eq('status', 'assigned')
        ?.lte('shift_start', now)
        ?.gte('shift_end', now)
        ?.single();

      if (error && error?.code !== 'PGRST116') throw error; // Ignore no rows found

      return { data: data || null, error: null };
    } catch (error) {
      console.error('Error getting current assignment:', error);
      return { data: null, error: error?.message };
    }
  },

  /**
   * Get staff assignments for today
   */
  async getTodayAssignments(staffId) {
    try {
      const today = new Date();
      const startOfDay = new Date(today.setHours(0, 0, 0, 0))?.toISOString();
      const endOfDay = new Date(today.setHours(23, 59, 59, 999))?.toISOString();
      
      const { data, error } = await supabase
        ?.from('staff_event_assignments')
        ?.select(`
          *,
          staff(
            *,
            user_profiles(*)
          ),
          events(*)
        `)
        ?.eq('staff_id', staffId)
        ?.gte('shift_start', startOfDay)
        ?.lte('shift_end', endOfDay)
        ?.order('shift_start', { ascending: true });

      if (error) throw error;

      return { data: data || [], error: null };
    } catch (error) {
      console.error('Error getting today assignments:', error);
      return { data: [], error: error?.message };
    }
  },

  /**
   * Update assignment notes (for check-in updates)
   */
  async updateAssignmentNotes(assignmentId, notes, userProfile) {
    try {
      if (!userProfile) {
        throw new Error('Unauthorized: User must be logged in');
      }

      const { data, error } = await supabase
        ?.from('staff_event_assignments')
        ?.update({
          notes: notes,
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', assignmentId)
        ?.select(`
          *,
          staff(
            *,
            user_profiles(*)
          ),
          events(*)
        `)
        ?.single();

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Error updating assignment notes:', error);
      return { data: null, error: error?.message };
    }
  }
};

export default staffService;